import React, { useState, useEffect } from 'react';
import { pwaManager, canInstallPWA, isPWAInstalled } from '../utils/pwaUtils';
import { Smartphone, X, Download, Wifi, WifiOff } from 'lucide-react';

const PWAInstallPrompt = () => {
  const [showPrompt, setShowPrompt] = useState(false);
  const [isInstalling, setIsInstalling] = useState(false);
  const [isOffline, setIsOffline] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    // Check if we should show the install prompt
    const shouldShowPrompt = canInstallPWA() && !isPWAInstalled() && !dismissed;
    setShowPrompt(shouldShowPrompt);

    // Set up PWA manager event listeners
    pwaManager.onInstallPromptAvailable = () => {
      if (!isPWAInstalled() && !dismissed) {
        setShowPrompt(true);
      }
    };

    pwaManager.onAppInstalled = () => {
      setShowPrompt(false);
      setIsInstalling(false);
    };

    pwaManager.onConnectionChange = (offline) => {
      setIsOffline(offline);
    };

    // Check initial network status
    const networkStatus = pwaManager.getNetworkStatus();
    setIsOffline(networkStatus.isOffline);

    return () => {
      // Clean up event listeners
      pwaManager.onInstallPromptAvailable = null;
      pwaManager.onAppInstalled = null;
      pwaManager.onConnectionChange = null;
    };
  }, [dismissed]);

  const handleInstall = async () => {
    if (!canInstallPWA()) return;

    setIsInstalling(true);
    try {
      const accepted = await pwaManager.promptInstall();
      if (accepted) {
        console.log('[PWA] User accepted install prompt');
      } else {
        console.log('[PWA] User declined install prompt');
        setShowPrompt(false);
      }
    } catch (error) {
      console.error('[PWA] Install failed:', error);
    } finally {
      setIsInstalling(false);
    }
  };

  const handleDismiss = () => {
    setDismissed(true);
    setShowPrompt(false);
  };

  const handleRetryShow = () => {
    setDismissed(false);
    if (canInstallPWA() && !isPWAInstalled()) {
      setShowPrompt(true);
    }
  };

  // Don't show if already installed or no install prompt available
  if (isPWAInstalled()) {
    return (
      <div className="fixed bottom-4 right-4 bg-green-600 text-white px-4 py-2 rounded-lg shadow-lg flex items-center gap-2 z-50">
        <Smartphone className="w-4 h-4" />
        <span className="text-sm font-medium">App Installed</span>
      </div>
    );
  }

  // Network status indicator
  if (isOffline) {
    return (
      <div className="fixed bottom-4 left-4 bg-orange-600 text-white px-4 py-2 rounded-lg shadow-lg flex items-center gap-2 z-50">
        <WifiOff className="w-4 h-4" />
        <span className="text-sm font-medium">Offline Mode</span>
      </div>
    );
  }

  // Install prompt
  if (!showPrompt || dismissed) {
    // Show small install button if dismissed but still installable
    if (canInstallPWA() && !dismissed) {
      return null;
    }
    
    // Show retry button if dismissed but user wants to install
    if (dismissed && canInstallPWA()) {
      return (
        <button
          onClick={handleRetryShow}
          className="fixed bottom-4 right-4 bg-blue-600 text-white p-3 rounded-full shadow-lg hover:bg-blue-700 transition-colors z-50"
          title="Install SPES Mobile App"
        >
          <Download className="w-5 h-5" />
        </button>
      );
    }
    
    return null;
  }

  return (
    <div className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-96 bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 p-4 z-50">
      <div className="flex items-start gap-3">
        {/* App Icon */}
        <div className="flex-shrink-0">
          <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center">
            <Smartphone className="w-6 h-6 text-white" />
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
            Install SPES Mobile App
          </h3>
          <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
            Get instant access to candidates and job descriptions. Works offline!
          </p>
          
          {/* Features */}
          <div className="flex items-center gap-2 mt-2">
            <div className="flex items-center gap-1">
              <Wifi className="w-3 h-3 text-green-500" />
              <span className="text-xs text-gray-600 dark:text-gray-400">Offline</span>
            </div>
            <div className="flex items-center gap-1">
              <Smartphone className="w-3 h-3 text-blue-500" />
              <span className="text-xs text-gray-600 dark:text-gray-400">Mobile</span>
            </div>
            <div className="flex items-center gap-1">
              <Download className="w-3 h-3 text-purple-500" />
              <span className="text-xs text-gray-600 dark:text-gray-400">Fast</span>
            </div>
          </div>
        </div>

        {/* Close button */}
        <button
          onClick={handleDismiss}
          className="flex-shrink-0 p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Action buttons */}
      <div className="flex gap-2 mt-3">
        <button
          onClick={handleInstall}
          disabled={isInstalling}
          className="flex-1 bg-blue-600 text-white px-3 py-2 rounded-md text-sm font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {isInstalling ? (
            <span className="flex items-center justify-center gap-2">
              <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              Installing...
            </span>
          ) : (
            'Install'
          )}
        </button>
        
        <button
          onClick={handleDismiss}
          className="px-3 py-2 text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors"
        >
          Not now
        </button>
      </div>
    </div>
  );
};

export default PWAInstallPrompt;
